import { useState, useEffect } from "react";
import { 
  auth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  googleProvider,
  signInWithPopup,
  signInWithCredential,
  updateProfile,
  sendPasswordResetEmail
} from "../firebase";
import { 
  Loader2, Mail, Lock, Eye, EyeOff, User, Gift, Shield, X
} from "lucide-react";

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [teamName, setTeamName] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resetSent, setResetSent] = useState(false);
  
  // Google sign-in modal state
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [googleUser, setGoogleUser] = useState(null);
  const [googleName, setGoogleName] = useState("");
  const [googleTeamName, setGoogleTeamName] = useState("");
  const [googleModalError, setGoogleModalError] = useState("");

  // Check URL for referral code
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('ref') || params.get('referral');
    if (code) {
      setReferralCode(code.toUpperCase());
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        // Validate all required fields
        if (!name.trim() || name.trim().length < 2) {
          setError("Name must be at least 2 characters");
          setLoading(false);
          return;
        }
        if (!teamName.trim() || teamName.trim().length < 3) {
          setError("Team name must be at least 3 characters");
          setLoading(false);
          return;
        }
        
        // Store name and team_name in sessionStorage BEFORE creating account
        // This ensures AuthContext can read them when onAuthStateChanged fires
        sessionStorage.setItem('signup_name', name.trim());
        sessionStorage.setItem('signup_team_name', teamName.trim());
        
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(cred.user, { displayName: name.trim() });
        window.gtag?.('event', 'sign_up', { method: 'email' });
        
        // Apply referral code after signup if provided
        if (referralCode.trim()) {
          try {
            const token = await cred.user.getIdToken();
            const API = process.env.REACT_APP_BACKEND_URL + '/api';
            await fetch(`${API}/referrals/apply?referral_code=${referralCode.trim().toUpperCase()}`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` }
            });
          } catch (refErr) {
            console.log("Referral code error:", refErr);
          }
        }
      }
    } catch (err) {
      setError(err.message.replace("Firebase: ", ""));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) { setError("Please enter your email address"); return; }
    setError("");
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
    } catch (err) {
      setError(err.message.replace("Firebase: ", ""));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);
    
    const API = process.env.REACT_APP_BACKEND_URL + '/api';
    
    try {
      // Detect native platform (iOS/Android)
      let isNativePlatform = false;
      try {
        const { Capacitor } = await import("@capacitor/core");
        isNativePlatform = Capacitor.isNativePlatform();
      } catch (e) {}
      
      let result;
      
      if (isNativePlatform) {
        // Native: use @capacitor-firebase/authentication
        // Requires: GoogleService-Info.plist (iOS) + REVERSED_CLIENT_ID URL scheme
        //           google-services.json (Android) + SHA-1 in Firebase Console
        try {
          const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
          const nativeResult = await FirebaseAuthentication.signInWithGoogle();
          console.log("FirebaseAuth result:", JSON.stringify(nativeResult));
          
          if (nativeResult?.credential?.idToken) {
            const credential = GoogleAuthProvider.credential(nativeResult.credential.idToken);
            result = await signInWithCredential(auth, credential);
          } else if (nativeResult?.user) {
            // Some versions return user directly without credential
            // The Firebase native SDK already signed in — sync with web SDK
            const credential = GoogleAuthProvider.credential(nativeResult.credential?.idToken || null, nativeResult.credential?.accessToken || null);
            result = await signInWithCredential(auth, credential);
          } else {
            throw new Error("No credential returned from native Google Sign-In");
          }
        } catch (nativeErr) {
          console.error("Native Google Sign-In error:", JSON.stringify(nativeErr), nativeErr?.message, nativeErr?.code);
          if (nativeErr?.message?.includes("canceled") || nativeErr?.message?.includes("cancelled") || 
              nativeErr?.code === "ERROR_CANCELED" || nativeErr?.message?.includes("12501")) {
            setLoading(false);
            return;
          }
          setError("Google Sign-In failed: " + (nativeErr?.message || "Unknown error. Check native plugin configuration."));
          setLoading(false);
          return;
        }
      } else {
        // Web: use signInWithPopup
        result = await signInWithPopup(auth, googleProvider);
      }
      const user = result.user;
      const token = await user.getIdToken();
      
      // Check if this user already exists in our backend and has a team name
      try {
        const checkRes = await fetch(`${API}/auth/check-user?firebase_uid=${user.uid}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const checkData = await checkRes.json();
        
        if (checkData.exists && checkData.has_team_name) {
          // Existing user with team name - proceed normally, AuthContext will handle the rest
          setLoading(false);
          return;
        }
        
        // New user OR existing user without team name - show modal to collect info
        // Sign out temporarily so AuthContext doesn't auto-register without the team name
        await auth.signOut();
        
        // Store the user info for the modal
        setGoogleUser(user);
        setGoogleName(user.displayName || "");
        setGoogleTeamName("");
        setShowGoogleModal(true);
        setLoading(false);
        
      } catch (checkErr) {
        console.log("Check user failed, showing modal:", checkErr);
        // If check fails, sign out and show modal to be safe
        await auth.signOut();
        setGoogleUser(user);
        setGoogleName(user.displayName || "");
        setGoogleTeamName("");
        setShowGoogleModal(true);
        setLoading(false);
      }
      
    } catch (err) {
      setError(err.message.replace("Firebase: ", ""));
      setLoading(false);
    }
  };

  const handleGoogleModalSubmit = async () => {
    setGoogleModalError("");
    
    // Validate
    if (!googleName.trim() || googleName.trim().length < 2) {
      setGoogleModalError("Name must be at least 2 characters");
      return;
    }
    if (!googleTeamName.trim() || googleTeamName.trim().length < 3) {
      setGoogleModalError("Team name must be at least 3 characters");
      return;
    }
    
    setLoading(true);
    
    try {
      // Store name and team_name in sessionStorage BEFORE signing in
      // AuthContext will pick these up when it registers the user
      sessionStorage.setItem('signup_name', googleName.trim());
      sessionStorage.setItem('signup_team_name', googleTeamName.trim());
      
      // Check if on native platform — use native Google Auth plugin
      let user = null;
      let isNativePlatform = false;
      try {
        const { Capacitor } = await import("@capacitor/core");
        isNativePlatform = Capacitor.isNativePlatform();
      } catch (e) {}
      
      if (isNativePlatform) {
        try {
          const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
          const nativeResult = await FirebaseAuthentication.signInWithGoogle();
          if (nativeResult?.credential?.idToken) {
            const credential = GoogleAuthProvider.credential(nativeResult.credential.idToken);
            const userCredential = await signInWithCredential(auth, credential);
            user = userCredential.user;
          } else if (nativeResult?.credential?.accessToken) {
            const credential = GoogleAuthProvider.credential(null, nativeResult.credential.accessToken);
            const userCredential = await signInWithCredential(auth, credential);
            user = userCredential.user;
          } else {
            throw new Error("No credential from native sign-in");
          }
        } catch (nativeErr) {
          if (nativeErr?.message?.includes("canceled") || nativeErr?.message?.includes("cancelled") || nativeErr?.code === "ERROR_CANCELED") {
            setLoading(false);
            return;
          }
          setGoogleModalError("Google Sign-In failed: " + (nativeErr?.message || "Check native config."));
          setLoading(false);
          return;
        }
      } else {
        const result = await signInWithPopup(auth, googleProvider);
        user = result.user;
      }
      
      // Update Firebase profile with the correct name
      if (googleName.trim() !== user.displayName) {
        await updateProfile(user, { displayName: googleName.trim() });
      }
      
      // Apply referral code if provided
      if (referralCode.trim()) {
        try {
          const token = await user.getIdToken();
          const API = process.env.REACT_APP_BACKEND_URL + '/api';
          await fetch(`${API}/referrals/apply?referral_code=${referralCode.trim().toUpperCase()}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
        } catch (refErr) {
          console.log("Referral code error:", refErr);
        }
      }
      
      setShowGoogleModal(false);
      setGoogleUser(null);
      
    } catch (err) {
      setGoogleModalError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelGoogleModal = async () => {
    // Sign out the Google user since they didn't complete registration
    try {
      await auth.signOut();
    } catch (e) {
      console.log("Sign out error:", e);
    }
    setShowGoogleModal(false);
    setGoogleUser(null);
    setGoogleName("");
    setGoogleTeamName("");
  };

  return (
    <div className="min-h-screen flex items-start justify-center px-4 pt-8 pb-24 overflow-y-auto" data-testid="auth-page">
      {/* Google Sign-In Modal */}
      {showGoogleModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Complete Your Profile</h2>
              <button onClick={handleCancelGoogleModal} className="text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Just one more step! Enter your name and team name to get started.
            </p>
            
            {googleModalError && (
              <div className="bg-red-500/20 border border-red-500/50 text-red-400 rounded-lg p-3 mb-4 text-sm">
                {googleModalError}
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Your Name *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    value={googleName}
                    onChange={(e) => setGoogleName(e.target.value)}
                    placeholder="Your name"
                    required
                    minLength={2}
                    className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
                    data-testid="google-name-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1">Team Name *</label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    value={googleTeamName}
                    onChange={(e) => setGoogleTeamName(e.target.value)}
                    placeholder="e.g., Super Kings XI"
                    required
                    minLength={3}
                    maxLength={30}
                    className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
                    data-testid="google-team-name-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">This will be your fantasy team name</p>
              </div>
              
              <button
                onClick={handleGoogleModalSubmit}
                disabled={loading}
                className="w-full py-3 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="google-modal-submit"
              >
                {loading ? <Loader2 className="animate-spin" size={20} /> : null}
                {loading ? "Creating Account..." : "Get Started"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="APL" className="w-16 h-16 mx-auto mb-3 rounded-xl" />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent mb-2">
            Apna Premier League
          </h1>
          <p className="text-slate-400">APL 2026 Fantasy</p>
        </div>

        <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6">
          {isForgotPassword ? (
            <>
              <h2 className="text-lg font-semibold text-white mb-1">Reset Password</h2>
              <p className="text-slate-400 text-sm mb-4">Enter your email and we'll send a reset link</p>
              {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-400 rounded-lg p-3 mb-4 text-sm">{error}</div>
              )}
              {resetSent ? (
                <div className="bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 rounded-lg p-4 text-sm mb-4">
                  Password reset email sent! Check your inbox for <strong>{email}</strong>.
                </div>
              ) : (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required
                        className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
                      />
                    </div>
                  </div>
                  <button type="submit" disabled={loading}
                    className="w-full py-2 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? <Loader2 className="animate-spin" size={18} /> : null}
                    Send Reset Link
                  </button>
                </form>
              )}
              <button onClick={() => { setIsForgotPassword(false); setResetSent(false); setError(""); }}
                className="w-full mt-4 text-slate-400 hover:text-white text-sm">
                Back to login
              </button>
            </>
          ) : (
            <>
              <div className="flex mb-6 border-b border-slate-700">
                <button onClick={() => setIsLogin(true)}
                  className={`flex-1 pb-2 text-sm font-medium transition-all ${isLogin ? 'text-emerald-400 border-b-2 border-emerald-400' : 'text-slate-400 hover:text-white'}`}>
                  Login
                </button>
                <button onClick={() => setIsLogin(false)}
                  className={`flex-1 pb-2 text-sm font-medium transition-all ${!isLogin ? 'text-emerald-400 border-b-2 border-emerald-400' : 'text-slate-400 hover:text-white'}`}>
                  Sign Up
                </button>
              </div>

              {error && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-400 rounded-lg p-3 mb-4 text-sm">{error}</div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <>
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">Name *</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Your name"
                          required
                          minLength={2}
                          className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
                          data-testid="signup-name-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">Team Name *</label>
                      <div className="relative">
                        <Shield className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input
                          type="text"
                          value={teamName}
                          onChange={(e) => setTeamName(e.target.value)}
                          placeholder="e.g., Super Kings XI"
                          required
                          minLength={3}
                          maxLength={30}
                          className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
                          data-testid="signup-team-name-input"
                        />
                      </div>
                      <p className="text-xs text-slate-500 mt-1">This will be your fantasy team name</p>
                    </div>
                  </>
                )}
                
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com" required
                      className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
                      data-testid="auth-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-1">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className="w-full pl-10 pr-10 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
                      data-testid="auth-password-input"
                      onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {!isLogin && (
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Referral Code (optional)</label>
                    <div className="relative">
                      <Gift className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                      <input type="text" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                        placeholder="FRIEND123" maxLength={10}
                        className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500 uppercase"
                        data-testid="signup-referral-input"
                      />
                    </div>
                  </div>
                )}

                <button type="submit" disabled={loading}
                  className="w-full py-2 bg-emerald-500 text-white font-semibold rounded-lg hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="auth-submit-btn"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : null}
                  {isLogin ? "Login" : "Create Account"}
                </button>
              </form>

              {isLogin && (
                <button onClick={() => setIsForgotPassword(true)}
                  className="w-full mt-3 text-slate-400 hover:text-emerald-400 text-sm">
                  Forgot password?
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
